import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  PLATFORM_ID,
  signal,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { initFlowbite } from 'flowbite';
import { filter } from 'rxjs';
import { AuthService } from './API/auth.service';
import { TermsAndConditionsAPIService } from './API/terms-and-conditions-api.service';
import { TermsAndConditions } from './models/terms-and-conditions.interface';
import { UserModel, UserRole } from './models/user.model';
import { ToastService } from './shared/toast/toast.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.html',
  standalone: false,
  styleUrl: './app.scss'
})
export class App implements OnInit, AfterViewInit {
  protected readonly title = signal('code-craft-learning-platform');
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly termsSectionElements = viewChildren<ElementRef<HTMLElement>>('termsSection');
  public readonly tobeSignedTerms = signal<TermsAndConditions[]>([]);
  public readonly currentUser = signal<UserModel | null>(null);
  public readonly sectionsRead = signal<boolean[]>([]);
  public readonly sectionsAccepted = signal<boolean[]>([]);
  public readonly activeSectionIndex = signal(0);
  public readonly currentTerms = computed(() => this.tobeSignedTerms()[0] ?? null);
  public readonly remainingSections = computed(() =>
    this.sectionsAccepted().filter((accepted) => !accepted).length,
  );
  public readonly canAcceptCurrentTerms = computed(() => {
    const terms = this.currentTerms();
    return !!terms &&
      terms.descriptions.length > 0 &&
      this.sectionsAccepted().length === terms.descriptions.length &&
      this.sectionsAccepted().every(Boolean);
  });
  public loaders = {
    termsAndConditions: signal(false),
    acceptingTerms: signal(false),
  };

  constructor(
    private authService: AuthService,
    private router: Router,
    private termsAndConditionsAPIService: TermsAndConditionsAPIService,
    private toastService: ToastService,
  ) {}

  ngOnInit(): void {
    const cachedUser = this.authService.getUser();

    if (!cachedUser) {
      this.authService.whoAmI().subscribe({
        next: (user) => {
          this.currentUser.set(user);
          this.redirectByRole(user.userRoles);
          this.verifyAgreement(user);
        },
      });
    } else {
      this.currentUser.set(cachedUser);
      this.verifyAgreement(cachedUser);
    }

  }

  private verifyAgreement(user: UserModel): void {
    if (user?.userAgreementAccepted) {
      return;
    }
    this.loaders.termsAndConditions.set(true);
    this.termsAndConditionsAPIService.getTermsByUserId(user.id).subscribe({
      next: (terms: TermsAndConditions[]) => {
        this.tobeSignedTerms.set(terms);
        this.resetCurrentTermsState();
        this.loaders.termsAndConditions.set(false);
      },
      error: () => {
        this.loaders.termsAndConditions.set(false);
      }
    });
  }

  public onSectionScroll(sectionIndex: number, event: Event): void {
    const element = event.currentTarget as HTMLElement;
    if (element.scrollHeight - element.scrollTop <= element.clientHeight + 2) {
      this.markSectionRead(sectionIndex);
    }
  }

  public setSectionAccepted(sectionIndex: number, event: Event): void {
    if (!this.sectionsRead()[sectionIndex]) {
      return;
    }

    const checked = (event.target as HTMLInputElement).checked;
    this.sectionsAccepted.update((accepted) =>
      accepted.map((value, index) => index === sectionIndex ? checked : value),
    );
  }

  public nextSection(): void {
    const sectionIndex = this.activeSectionIndex();
    const lastSectionIndex = (this.currentTerms()?.descriptions.length ?? 0) - 1;
    if (!this.sectionsAccepted()[sectionIndex] || sectionIndex >= lastSectionIndex) {
      return;
    }
    this.selectSection(sectionIndex + 1);
  }

  public selectSection(sectionIndex: number): void {
    const firstUnacceptedIndex = this.sectionsAccepted().findIndex((accepted) => !accepted);
    const lastAvailableIndex = firstUnacceptedIndex === -1
      ? this.sectionsAccepted().length - 1
      : firstUnacceptedIndex;
    if (sectionIndex < 0 || sectionIndex > lastAvailableIndex) {
      return;
    }

    this.activeSectionIndex.set(sectionIndex);
    setTimeout(() => this.updateActiveSectionReadState());
  }

  public previousSection(): void {
    this.selectSection(this.activeSectionIndex() - 1);
  }

  public acceptCurrentTerms(): void {
    const user = this.currentUser();
    const terms = this.currentTerms();
    if (!user || !terms || !this.canAcceptCurrentTerms() || this.loaders.acceptingTerms()) {
      return;
    }

    this.loaders.acceptingTerms.set(true);
    this.termsAndConditionsAPIService.acceptTerms(user.id, terms.id).subscribe({
      next: (updatedUser) => {
        this.currentUser.set(updatedUser);
        this.authService.saveUser(updatedUser);

        if (this.tobeSignedTerms().length > 1) {
          this.tobeSignedTerms.update((pendingTerms) => pendingTerms.slice(1));
          this.resetCurrentTermsState();
          return;
        }

        if (updatedUser.userAgreementAccepted) {
          this.tobeSignedTerms.set([]);
          this.sectionsRead.set([]);
          this.sectionsAccepted.set([]);
          this.activeSectionIndex.set(0);
          return;
        }

        this.toastService.error('All required terms must be accepted before continuing.');
      },
      error: () => this.loaders.acceptingTerms.set(false),
      complete: () => this.loaders.acceptingTerms.set(false),
    });
  }

  private resetCurrentTermsState(): void {
    const sectionCount = this.currentTerms()?.descriptions.length ?? 0;
    this.sectionsRead.set(Array(sectionCount).fill(false));
    this.sectionsAccepted.set(Array(sectionCount).fill(false));
    this.activeSectionIndex.set(0);
    setTimeout(() => this.updateActiveSectionReadState());
  }

  private updateActiveSectionReadState(): void {
    const element = this.termsSectionElements().at(0)?.nativeElement;
    if (element && element.scrollHeight <= element.clientHeight + 2) {
      this.markSectionRead(this.activeSectionIndex());
    }
  }

  private markSectionRead(sectionIndex: number): void {
    if (this.sectionsRead()[sectionIndex]) {
      return;
    }
    this.sectionsRead.update((readSections) =>
      readSections.map((read, index) => index === sectionIndex ? true : read),
    );
  }

  ngAfterViewInit(): void {
    this.initializeFlowbite();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.initializeFlowbite());
  }

  private initializeFlowbite(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    setTimeout(() => initFlowbite());
  }

  private redirectByRole(roles: UserRole[]): void {
    if (roles.filter(role => role.role === 'CCL_ADMIN' || role.role === 'CCL_OPS').length > 0) {
      this.router.navigate(['/admin']);
    } else if (roles.filter(role => role.role === 'STUDENT').length > 0) {
      this.router.navigate(['/student']);
    } else if (roles.filter(role => role.role === 'TRAINER').length > 0) {
      this.router.navigate(['/trainer']);
    } else {
      this.router.navigate(['/']);
    }
  }
}
